import { useId, useState, useEffect } from "react";
import { CloudUpload } from "lucide-react";
import { UploadedFilesUI } from "@/shared/components/ui/Upload/UploadedFilesUI";

export default function UploadOnboarding({
  type = "file",
  defaultImageUrl = "",
  onFilesChange,
  initialFiles = [],
  handleSubmitButtonClick,
  handleDelete: handleDeleteFromParent,
  handleDescriptionChange: handleDescriptionChangeFromParent,
  uploadedFileIds = new Set(),
  uploadingFileId = null,
  uploadProgress = {},
  isEditMode = false,
  localOnlyMode = false,
}) {
  const [files, setFiles] = useState([]);
  const [uploadedImage, setUploadedImage] = useState(null);
  const inputId = useId();

  // Stable dependency: only re-sync when file list identity actually changes
  const initialFilesSignature =
    initialFiles?.length +
    "-" +
    (initialFiles?.map((f) => f.id || f.assetId || f.name || "").join("|") ||
      "");

  // Use initialFilesSignature (content-based) to avoid re-running when parent passes new array ref
  useEffect(() => {
    if (initialFiles && initialFiles.length > 0) {
      const initialFilesData = initialFiles
        .map((fileItem, idx) => {
          if (typeof fileItem === "string") {
            return {
              id: Date.now() + idx,
              name: fileItem,
              size: 0,
              type: "",
              progress: 100,
              completed: true,
              description: "",
            };
          } else if (fileItem && typeof fileItem === "object") {
            // Existing assets have assetId (UUID); new files have file object
            const assetId = fileItem.assetId;
            const fileId = assetId
              ? `existing-${assetId}`
              : fileItem.id || `file-${Date.now()}-${idx}`;

            // Ensure file_size is a number
            const fileSize = fileItem.file_size
              ? typeof fileItem.file_size === "number"
                ? fileItem.file_size
                : parseInt(fileItem.file_size, 10)
              : fileItem.file?.size || 0;

            return {
              id: fileId,
              name: fileItem.file_name || fileItem.name || "",
              size: fileSize,
              type: fileItem.mime_type || fileItem.file?.type || "",
              progress: 100,
              completed: true,
              description: fileItem.context ?? fileItem.description ?? "",
              assetId: assetId,
              file: fileItem.file,
            };
          }
          return null;
        })
        .filter(Boolean);

      setFiles(initialFilesData);
    } else if (initialFiles && initialFiles.length === 0) {
      // Clear files if initialFiles is empty
      setFiles([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initialFilesSignature is derived from initialFiles
  }, [initialFilesSignature]);

  const handleFileUpload = (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (type.trim() === "image") {
      const imageFile = selectedFiles[0];
      if (imageFile) {
        const objectURL = URL.createObjectURL(imageFile);
        setUploadedImage(objectURL);
      }
    } else if (type === "file") {
      const newFiles = selectedFiles.map((file, idx) => ({
        id: Date.now() + idx,
        name: file.name,
        size: file.size,
        type: file.type,
        progress: 100,
        completed: true,
        description: "",
        file: file,
      }));

      const updatedFiles = [...files, ...newFiles];
      setFiles(updatedFiles);
      if (onFilesChange) {
        onFilesChange(updatedFiles);
      }
    }
  };

  const handleDelete = (id) => {
    if (handleDeleteFromParent) {
      handleDeleteFromParent(id);
    }
    const updatedFiles = files.filter((file) => file.id !== id);
    setFiles(updatedFiles);
    if (type === "image") setUploadedImage(null);
    if (onFilesChange) {
      onFilesChange(updatedFiles);
    }
  };

  // Update description for a specific file
  const handleDescriptionChange = (fileId, value) => {
    setFiles((prevFiles) => {
      const updated = prevFiles.map((file) => {
        // Ensure both IDs are compared as strings to avoid type mismatch
        const currentFileId =
          typeof file.id === "string" ? file.id : String(file.id || "");
        const targetFileId =
          typeof fileId === "string" ? fileId : String(fileId || "");
        if (currentFileId === targetFileId) {
          return { ...file, description: value };
        }
        return file;
      });
      // Notify parent component about the updated files with new descriptions
      if (onFilesChange) {
        onFilesChange(updated);
      }
      return updated;
    });
  };

  return (
    <div className="w-full">
      <div className="flex w-full gap-[18px] items-center">
        {/* Show uploaded or default image */}
        {type === "image" && (
          <img
            className="w-14 h-14 rounded-full"
            src={uploadedImage || defaultImageUrl}
            alt="profile"
          />
        )}

        {/* Upload Box */}
        <div
          className="border border-gray-300 rounded-md p-6 text-center cursor-pointer w-full"
          onClick={() => document.getElementById(inputId).click()}
        >
          <input
            id={inputId}
            type="file"
            accept={
              type === "file"
                ? ".pdf,.csv,.pptx,.docx,.txt"
                : type === "image"
                  ? "image/*"
                  : "*"
            }
            multiple={type !== "image"}
            className="hidden"
            onChange={handleFileUpload}
          />
          <div className="flex flex-col items-center justify-center">
            <CloudUpload className="w-10 h-10 text-gray-500 mb-2 bg-neutral-100 rounded-full p-2" />
            <p className="text-gray-500  text-xs font-normal">
              <span className=" text-secondary font-semibold underline">
                Click to upload
              </span>{" "}
              or drag and drop
            </p>
            <p className="mt-1 text-gray-500 text-[10px]">
              PPTX, PDF, JPG, PNG, MP4 (max. 25mb each)
            </p>
          </div>
        </div>
      </div>

      {/* File List for general file upload */}
      {type === "file" && files.length > 0 && (
        <div className="mt-6 space-y-3">
          {files.map((file, index) => {
            const isUploaded = uploadedFileIds.has(file.id);
            const isUploading = uploadingFileId === file.id;
            const progress = uploadProgress[file.id] || 0;

            return (
              <UploadedFilesUI
                key={file.id || `file-${index}`}
                file={file}
                handleDelete={handleDelete}
                handleSubmitButtonClick={handleSubmitButtonClick}
                handleDescriptionChange={(fileId, value) => {
                  handleDescriptionChange(fileId, value);
                  handleDescriptionChangeFromParent?.(fileId, value);
                }}
                description={file.description || ""}
                isUploaded={isUploaded}
                isUploading={isUploading}
                uploadProgress={progress}
                isEditMode={isEditMode}
                localOnlyMode={localOnlyMode}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
